/**
 * Fallback view rendered inside the embed iframe when the parent page's
 * Permissions-Policy blocks camera or microphone. Offers a single
 * "Continue in new tab" action that opens the standard /join flow as a
 * top-level browsing context where Permissions-Policy doesn't apply.
 *
 * @module components/embed/EmbedPermissionFallback
 */

import React from 'react';

interface EmbedPermissionFallbackProps {
  /** Campaign hash to navigate to in the new tab. */
  hash: string;
  /**
   * Origin to open the new tab on. Defaults to the iframe's own origin
   * which matches the snippet's served-from domain.
   */
  baseUrl?: string;
  /** Notifies the parent so it can close the iframe modal. */
  onOpenedExternally?: () => void;
  /** Keep the current response when text participation is available. */
  onContinueInText?: () => void;
}

const EmbedPermissionFallback: React.FC<EmbedPermissionFallbackProps> = ({
  hash,
  baseUrl,
  onOpenedExternally,
  onContinueInText,
}) => {
  const handleContinue = () => {
    const origin = baseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');
    const target = `${origin}/join/${encodeURIComponent(hash)}`;
    window.open(target, '_blank', 'noopener,noreferrer');
    onOpenedExternally?.();
  };

  return (
    <div className="min-h-[320px] bg-background text-foreground flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <h1 className="text-2xl font-bold mb-3">Open in a new tab</h1>
        <p className="text-muted-foreground mb-8">
          This site blocks camera or microphone access. Your current answers stay here; a new tab starts a new response.
        </p>
        <button
          type="button"
          onClick={handleContinue}
          className="w-full px-8 py-4 button-accent font-bold text-lg rounded-lg transition-colors"
        >
          Start new response
        </button>
        {onContinueInText && <button type="button" onClick={onContinueInText} className="mt-4 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-surface-hover">Continue in text</button>}
      </div>
    </div>
  );
};

export default EmbedPermissionFallback;
